import { useState, useCallback } from 'react';
import {
  createKitsTemplate,
  getKitById,
  getKitsTemplatesByKitId,
  deleteKitsTemplateById,
} from 'api/kits';

export const useKitsTemplates = (kitId: string) => {
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKit, setSelectedKit] = useState<any>(null);
  const [existingTemplate, setExistingTemplate] = useState<any>(null);

  const fetchKitsData = useCallback(async () => {
    try {
      setLoading(true);
      const kitData = await getKitById(kitId);
      setSelectedKit(kitData?.data?.result);
    } catch (err) {
      setError('Failed to fetch kit data');
    } finally {
      setLoading(false);
    }
  }, [kitId]);

  const fetchTemplateByKitId = useCallback(async () => {
    try {
      setLoading(true);
      const templates = await getKitsTemplatesByKitId(kitId);
      if (templates?.data?.result?.length > 0) {
        setExistingTemplate(templates?.data?.result[0]);
      }
    } catch (err) {
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [kitId]);

  const saveTemplate = useCallback(
    async (editor: any) => {
      if (!editor) return;

      const html = editor.getHtml();
      const css = editor.getCss();
      const projectJSON = await editor.store();

      const templateData = {
        KitId: +kitId,
        html,
        css,
        templateMeta: {
          projectJSON,
        },
      };

      try {
        setIsSaving(true);
        const response = await createKitsTemplate(templateData);
        setExistingTemplate(response.data?.result);
      } catch (err) {
        setError('Failed to save template');
      } finally {
        setIsSaving(false);
      }
    },
    [kitId],
  );

  const deleteTemplate = useCallback(async () => {
    if (!existingTemplate) return;

    try {
      setIsDeleting(true);
      await deleteKitsTemplateById(existingTemplate.Id);
      setExistingTemplate(null);
    } catch (err) {
      setError('Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  }, [existingTemplate]);

  return {
    loading,
    isSaving,
    isDeleting,
    error,
    selectedKit,
    existingTemplate,
    fetchKitsData,
    fetchTemplateByKitId,
    saveTemplate,
    deleteTemplate,
  };
};
